import { FC, useState } from 'react'

import { useQuery, useSuspenseQueries } from '@tanstack/react-query'

import { Cluster, Spinner, TextField, useToaster } from '@forto-dev/phoenix-ui-core'

import { RequiredActionV2 } from '@/features/workbench'
import { useLocalisation } from '@/utils/localisation'

import { houseShipmentByCodeQueryOptions } from '../../api/houseShipments.service'
import { shipmentCargoSummaryOptions, shipmentQueryOptions } from '../../api/useShipment'
import {
  useHouseShipmentContainerNumberUpdate,
  useShipmentInformationUpdate,
} from '../../api/useShipmentInformationUpdate'
import { isValidContainerNumber } from '../../utils/isValidContainerNumber'

type ActionAddContainerNumbersProps = {
  action: RequiredActionV2
  setSubmitCallback: (cb: (() => Promise<void>) | undefined) => void
}

export const ActionAddContainerNumbers: FC<ActionAddContainerNumbersProps> = ({ action, setSubmitCallback }) => {
  const toaster = useToaster()

  const [{ data: shipment }] = useSuspenseQueries({
    queries: [houseShipmentByCodeQueryOptions(action.entityId)],
  })

  const { data, isLoading } = useQuery(shipmentQueryOptions(action.entityId, !shipment.v2Flow))
  const { data: dataV2, isLoading: isLoadingV2 } = useQuery(
    shipmentCargoSummaryOptions(action.entityId, shipment.v2Flow)
  )
  const { translate } = useLocalisation()
  const updateMutation = useShipmentInformationUpdate()
  const updateMutationV2 = useHouseShipmentContainerNumberUpdate()

  const [errors, setErrors] = useState<Record<string, boolean>>({})
  const [containerNumberMap, setContainerNumberMap] = useState<Record<string, string>>({})

  if (isLoading || isLoadingV2) {
    return (
      <Cluster x="center">
        <Spinner />
      </Cluster>
    )
  }

  if (!data && !dataV2) {
    return null
  }

  const updateContainerNumbers = async (newContainerMap: Record<string, string>) => {
    try {
      const containers = data?.containers.map((container) => ({
        ...container,
        containerNumber: newContainerMap[container._id],
      }))
      await updateMutation.mutateAsync({
        shipmentId: action.entityId,
        data: {
          containers,
        },
      })
      toaster({
        title: translate('required-actions.dialog.containerNumberUpdateSuccess'),
        variant: 'positive',
      })
    } catch (error) {
      toaster({
        title: translate('required-actions.dialog.containerNumberUpdateError'),
        description: error instanceof Error ? error.message : translate('errorDescription'),
        variant: 'negative',
      })
    }
  }

  const updateV2ContainerNumbers = async (newContainerMap: Record<string, string>) => {
    try {
      const updatePromises = Object.entries(newContainerMap).map(async ([containerId, newContainerNumber]) => {
        if (newContainerNumber) {
          await updateMutationV2.mutateAsync({
            houseShipmentCode: action.entityId,
            houseCargoID: containerId,
            data: {
              containerNumber: newContainerNumber,
            },
          })
        }
      })

      await Promise.all(updatePromises)

      toaster({
        title: translate('required-actions.dialog.containerNumberUpdateSuccess'),
        variant: 'positive',
      })

      setContainerNumberMap({})
    } catch (error) {
      toaster({
        title: translate('required-actions.dialog.containerNumberUpdateError'),
        description: error instanceof Error ? error.message : translate('errorDescription'),
        variant: 'negative',
      })
    }
  }

  const onChange = (containerId: string, newContainerNumber: string) => {
    const updatedErrors = { ...errors }
    const isValid = isValidContainerNumber(newContainerNumber)
    updatedErrors[containerId] = !isValid
    setSubmitCallback(undefined)

    if (isValid) {
      const newContainerMap = { ...containerNumberMap, [containerId]: newContainerNumber }
      setContainerNumberMap(newContainerMap)
      updatedErrors[containerId] = false

      let allContainerNumbersFilled = false
      const noErrors = Object.values(updatedErrors).every((error) => !error)

      if (shipment.v2Flow && dataV2) {
        allContainerNumbersFilled = dataV2.every((container) => newContainerMap[container.ID])
      } else {
        allContainerNumbersFilled = data?.containers.every((container) => newContainerMap[container._id]) || false
      }

      if (allContainerNumbersFilled && noErrors) {
        if (shipment.v2Flow && dataV2) {
          setSubmitCallback(() => updateV2ContainerNumbers(newContainerMap))
        } else {
          setSubmitCallback(() => updateContainerNumbers(newContainerMap))
        }
      }
    }
    setErrors(updatedErrors)
  }

  if (shipment.v2Flow && dataV2) {
    return dataV2.map((container, index) => {
      const containerId = container.ID

      return (
        <TextField
          key={containerId}
          label={`#${index + 1} ${translate('required-actions.dialog.containerNumber')}`}
          required
          defaultValue={containerNumberMap[containerId]}
          placeholder={translate('required-actions.dialog.containerNumberPlaceholder')}
          onChange={(e) => onChange(containerId, e.target.value)}
          state={errors?.[containerId] ? 'negative' : undefined}
          hint={errors?.[containerId] ? translate('required-actions.dialog.containerValidationError') : undefined}
        />
      )
    })
  }

  return data?.containers.map((container, index) => {
    return (
      <TextField
        key={container._id}
        label={`#${index + 1} ${translate('required-actions.dialog.containerNumber')}`}
        required
        defaultValue={containerNumberMap[container._id]}
        placeholder={translate('required-actions.dialog.containerNumberPlaceholder')}
        onChange={(e) => onChange(container._id, e.target.value)}
        state={errors?.[container._id] ? 'negative' : undefined}
        hint={errors?.[container._id] ? translate('required-actions.dialog.containerValidationError') : undefined}
      />
    )
  })
}
